/**
 * Created by Ning Jiang on 2021-05-05.
 */

import {TombstoneRip} from "../TombstoneRip";
import {Layers, TombstoneRipLayers} from "./TombstoneRipLayers";
import {gsap, TimelineLite} from "gsap";
import {TombstoneRipGameMode} from "../gameflow/TombstoneRipGameMode";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {SuperFsBackgroundParticles} from "./SuperFsBackgroundParticles";
import {ShootingController} from "./ShootingController";

export class TombstoneRipBackground extends PIXI.Container {

    private _backgroundMain:PIXI.Sprite;
    private _mainIdle:PIXI.spine.Spine;
    private _bgAnimationContainer:PIXI.Container;

    private _foregroundMain:PIXI.Sprite;
    private _foregroundFs:PIXI.Sprite;
    private _foregroundFs_Super:PIXI.Sprite;

    private _superFsDustContainer:PIXI.Container;
    private _superFsFire:PIXI.spine.Spine;
    private _superFsDust:PIXI.spine.Spine;
    private _superMatch:PIXI.spine.Spine | null;
    private _bgParticles:SuperFsBackgroundParticles;

    private static _stageShakeTl:TimelineLite | null;

    public currentMode:string = GameMode.NORMAL;

    public get foregroundFs_Super():PIXI.Sprite {
        return this._foregroundFs_Super;
    }

    constructor() {
        super();
        this.init();
    }

    private init() {
        this._backgroundMain = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("Background_NORMAL")[0]);
        this._backgroundMain.position.set(360, 360);
        this._backgroundMain.anchor.set(0.5, 0.5);
        this.addChild(this._backgroundMain);

        this._bgAnimationContainer = new PIXI.Container();
        this._bgAnimationContainer.position.set(360, 360);
        this.addChild(this._bgAnimationContainer);
        TombstoneRipLayers.getLayer(Layers.BACKGROUND)?.addChild(this);
        this.startMainIdle();

        this._foregroundMain = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("Reel_area_NORMAL")[0]);
        this._foregroundMain.anchor.set(0.5, 1);
        this._foregroundMain.position.set(361.5, 893);
        TombstoneRipLayers.getLayer(Layers.FOREGROUND)?.addChild(this._foregroundMain);

        this._superFsDustContainer = new PIXI.Container();
        this._superFsDustContainer.position.set(360, 360);
        TombstoneRipLayers.getLayer(Layers.FOREGROUND)?.addChild(this._superFsDustContainer);

        this._foregroundFs = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures(`Foreground_FREESPIN`)[0]);
        this._foregroundFs.anchor.set(0.5, 0);
        this._foregroundFs.position.set(350, 750);
        this._foregroundFs.visible = false;
        TombstoneRipLayers.getLayer(Layers.FOREGROUND_TOP)?.addChild(this._foregroundFs);

        this._foregroundFs_Super = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures(`Foreground_FREESPIN_SUPER`)[0]);
        this._foregroundFs_Super.anchor.set(0.5, 0);
        this._foregroundFs_Super.position.set(360, 670);
        this._foregroundFs_Super.visible = false;
        TombstoneRipLayers.getLayer(Layers.FOREGROUND_TOP)?.addChild(this._foregroundFs_Super);

        this.onOrientationChanged();
        this.onResize();
    }

    public onOrientationChanged() {
        ShootingController.onOrientationChanged();
    }

    public onResize() {
        // TODO: check restore, mode, orientation
    }

    public updateEnvironment(mode:string):void {
        this.currentMode = mode;
        this._backgroundMain.texture = TombstoneRip.assetLoader.getTextures(`Background_${mode}`)[0];
        this._foregroundMain.texture = TombstoneRip.assetLoader.getTextures(`Reel_area_${mode}`)[0];
        this._foregroundFs.visible = mode === GameMode.FREESPIN;
        this._foregroundFs_Super.visible = mode === TombstoneRipGameMode.FREESPIN_SUPER;
        if(mode.includes("SUPER")) {
            if(!this._bgParticles) {
                this._bgParticles = new SuperFsBackgroundParticles();
                this._bgParticles.position.set(0, 100);
                this._backgroundMain.addChild(this._bgParticles);
            }
            this._bgParticles.play();
            this.startSuperFsBG();
            TombstoneRip.reel5Frame.startSuperFs(TombstoneRipGameMode.FREESPIN_SUPER);
        } else {
            if(this._bgParticles) {
                this._bgParticles.stop();
            }
            this.endSuperFsBG();
            TombstoneRip.reel5Frame.endSuperFs();
        }
        if(mode === GameMode.NORMAL) {
            this.startMainIdle();
        } else {
            this.stopMainIdle();
        }
        let color = this.getModeColor(mode);
        TombstoneRip.initialCountUp.setColor(color);
        TombstoneRip.bigWinCountUp.setColor(color);
        TombstoneRip.waysWon.setColor(color);
        if(mode.includes("SUPER")) {
            color = 0xffffff;
        }
        TombstoneRip.ways.setColor(color, mode.includes("SUPER"));
        TombstoneRip.multiplier.setColor(color, mode.includes("SUPER"));
    }

    private getModeColor(mode:string):number {
        switch(mode) {
            case TombstoneRipGameMode.FREESPIN_SUPER:
            case TombstoneRipGameMode.NORMAL_SUPER:
                return 0xeaac11;
            case GameMode.FREESPIN:
                return 0xe7a96e;
            default:
                return 0xffffff;
        }
    }

    private startMainIdle():void {
        if(!this._mainIdle) {
            this._mainIdle = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("mainIdle"));
            this._mainIdle.autoUpdate = true;
        }

        this._bgAnimationContainer.addChild(this._mainIdle);
        this._mainIdle.state.setAnimation(0, "idle", true);
        this._mainIdle.state.timeScale = 0.1;
    }

    private stopMainIdle():void {
        if(this._mainIdle) {
            this._mainIdle.state.clearTrack(0);
            this._mainIdle.skeleton.setToSetupPose();
            this._mainIdle.state.setEmptyAnimation(0, 0);
            this._bgAnimationContainer.removeChild(this._mainIdle);
        }
    }

    private startSuperFsBG():void {
        if(!this._superFsFire) {
            this._superFsFire = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("superFSFire"));
            this._superFsFire.autoUpdate = true;
        }
        if(!this._superFsDust) {
            this._superFsDust = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("superFSDust"));
            this._superFsDust.autoUpdate = true;
        }
        this._bgAnimationContainer.addChild(this._superFsFire);
        this._superFsFire.state.setAnimation(0, "SFfire", true);
        this._superFsDustContainer.addChild(this._superFsDust);
        this._superFsDust.state.setAnimation(0, "SFdust", true);
    }

    private endSuperFsBG():void {
        if(this._superFsFire) {
            this._superFsFire.state.clearTrack(0);
            this._superFsFire.skeleton.setToSetupPose();
            this._superFsFire.state.setEmptyAnimation(0, 0);
            this._bgAnimationContainer.removeChild(this._superFsFire);
        }
        if(this._superFsDust) {
            this._superFsDust.state.clearTrack(0);
            this._superFsDust.skeleton.setToSetupPose();
            this._superFsDust.state.setEmptyAnimation(0, 0);
            this._superFsDustContainer.removeChild(this._superFsDust);
        }
    }

    public playSuperFsMatch():void {
        this._superMatch = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("superMatchBg"));
        this._superMatch.autoUpdate = true;

        const shakeTl = new TimelineLite({paused : true});
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 4, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : -4, y : 4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 4, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : -4, y : 4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : -4, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));
        shakeTl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.066}));


        const tl = new TimelineLite();
        tl.add(() => {
            this._bgAnimationContainer.addChild(this._superMatch!);
            this._superMatch!.state.setAnimation(0, "explosion", false);
            TombstoneRipBackground.moveStage(shakeTl);
        })
        tl.add(gsap.to(this, {duration : this._superMatch.spineData.findAnimation("explosion").duration}));
        tl.add(() => {
            this._superMatch!.state.setEmptyAnimation(0, 0);
            this._bgAnimationContainer.removeChild(this._superMatch!);
            this._superMatch?.destroy();
            this._superMatch = null;
        });
    }

    public static moveStage(tl:TimelineLite):void {
        this.stopStageMove();
        tl.play();
        this._stageShakeTl = tl;
    }

    public static stopStageMove():void {
        if(this._stageShakeTl && this._stageShakeTl.isActive()) {
            this._stageShakeTl.progress(1);
        }
        this._stageShakeTl = null;
        // TombstoneRipBackground.stage.position.set(Math.round(NolimitApplication.screenBounds.left), Math.round(NolimitApplication.screenBounds.top));
    }

    public static get stage():PIXI.Container {
        return TombstoneRipLayers.getLayer(Layers.BACKGROUND)!.parent!;
    }

    public static shakeStageOnce(factor?:number, timeScale?:number):void {
        if(!factor) {
            factor = 1;
        }
        const tl = new TimelineLite({paused : true});
        tl.add(gsap.to(TombstoneRipBackground.stage, {x : 16 * factor, y : -8 * factor, duration : 0.033}));
        tl.add(gsap.to(TombstoneRipBackground.stage, {x : 0, y : 0, duration : 0.267}));
        if(timeScale) {
            tl.timeScale(timeScale);
        }
        TombstoneRipBackground.moveStage(tl);
    }
}